import {
  Component,
  EventEmitter,
  Output,
  DestroyRef,
  inject,
  ViewEncapsulation,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges
} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {catchError, concatMap, filter, forkJoin, of} from 'rxjs';
import {UserInterface} from 'src/app/services/users/interface/user.interface';
import {UsersService} from 'src/app/services/users/users.service';
import {passwordMatchValidator} from '../../validators/validators';
import {ToastrService} from 'ngx-toastr';

@Component({
  selector: 'app-user-modal',
  templateUrl: './user-modal.component.html',
  styleUrls: ['./user-modal.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class UserModalComponent implements OnInit, OnChanges {
  @Input() userToEdit: UserInterface | null = null;
  @Output() closeModal = new EventEmitter<boolean>(false);
  @Output() userCreatedOrUpdated = new EventEmitter<void>();
  userForm: FormGroup;
  roles: any[] = [];
  subcalendars: { id: number, description: string }[] = [];
  submitted: boolean;
  installers: any[] = [];
  zohoOwners: any[] = [];
  teamUpWho: any[] = [];
  private destroyRef = inject(DestroyRef);

  constructor(private fb: FormBuilder,
              private usersService: UsersService,
              private toastr: ToastrService) {
  }

  ngOnInit(): void {
    this.getData();
  }

  get isInstallerRole(): boolean {
    return this.userForm?.get('role_id')?.value === 5;
  }

  get isDesignerRole(): boolean {
    return this.userForm?.get('role_id')?.value === 6;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['userToEdit'] && this.userForm) {
      this.userForm.patchValue({
        name: this.userToEdit ? this.userToEdit.name : null,
        email: this.userToEdit ? this.userToEdit.email : null,
        role_id: this.userToEdit ? this.userToEdit.role_id : null,
        subcalendar_id: this.userToEdit && this.userToEdit.subcalendar_id ? this.userToEdit.subcalendar_id : null,
        phone: this.userToEdit ? this.userToEdit.phone : null,
        zoho_contact_owner_id: this.userToEdit ? (this.userToEdit as any).zoho_contact_owner_id : null,
        teamup_job_owner_id: this.userToEdit ? (this.userToEdit as any).teamup_job_owner_id : null,
      });

      if (this.userToEdit) {
        this.userForm.get('password')?.clearValidators();
        this.userForm.get('password')?.setValidators([Validators.minLength(8), Validators.maxLength(64)]);
        this.userForm.get('confirm_password')?.clearValidators();
      } else {
        this.userForm.get('password')?.setValidators([Validators.required, Validators.minLength(8), Validators.maxLength(64)]);
        this.userForm.get('confirm_password')?.setValidators([Validators.required]);
      }
      this.userForm.get('password')?.updateValueAndValidity({emitEvent: false});
      this.userForm.get('confirm_password')?.updateValueAndValidity({emitEvent: false});
    }
  }

  createUser(): void {
    this.submitted = true;

    if (this.userForm.invalid) {
      this.handleInvalidFormSubmission();
      return;
    }

    this.usersService.createUser(this.userForm.value).pipe(
      concatMap(() => this.usersService.getUsers()),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: () => {
        this.userToEdit = null;
        this.userCreatedOrUpdated.emit();
      },
      complete: () => {
        this.userToEdit = null;
        this.closeModal.emit(true);
        this.submitted = false;
      }
    });
  }

  updateUser(): void {
    this.submitted = true;

    if (this.userForm.invalid) {
      this.handleInvalidFormSubmission();
      return;
    }

    let userForUpdate = this.userForm.value;
    if (!this.userForm.get('password')?.value) {
      delete userForUpdate['password'];
      delete userForUpdate['confirm_password'];
    }

    this.usersService.updateUser(this.userForm.value, this.userToEdit?.id!).pipe(
      concatMap(() => this.usersService.getUsers()),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: () => {
        this.userToEdit = null;
        this.userCreatedOrUpdated.emit();
      },
      complete: () => {
        this.userToEdit = null;
        this.closeModal.emit(true);
        this.submitted = false;
      }
    });
  }

  private buildForm() {
    this.userForm = this.fb.group({
      name: [this.userToEdit ? this.userToEdit.name : null, Validators.required],
      email: [this.userToEdit ? this.userToEdit.email : null, [Validators.required, Validators.email]],
      password: [null, this.userToEdit ? [Validators.minLength(8), Validators.maxLength(64)] :
        [Validators.required, Validators.minLength(8), Validators.maxLength(64)]],
      confirm_password: [null, this.userToEdit ? [] : [Validators.required]],
      role_id: [this.userToEdit ? this.userToEdit.role_id : null, Validators.required],
      subcalendar_id: [this.userToEdit && this.userToEdit['subcalendar_id'] ? this.userToEdit['subcalendar_id'] : null],
      zoho_contact_owner_id: [this.userToEdit ? (this.userToEdit as any).zoho_contact_owner_id : null],
      teamup_job_owner_id: [this.userToEdit ? (this.userToEdit as any).teamup_job_owner_id : null],
      phone: [this.userToEdit ? this.userToEdit.phone : null, Validators.required]
    }, {validator: passwordMatchValidator});

    this.passwordValueChangesSubscription();
    this.roleChangesSubscription();

    // Apply initial role-based validation
    const currentRole = this.userForm.get('role_id')?.value;
    if (currentRole) {
      this.applyRoleValidation(currentRole);
    }
  }

  private passwordValueChangesSubscription(): void {
    this.userForm.get('password')?.valueChanges.pipe(
      filter(() => !!this.userToEdit),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((value: string) => {
      if (!value) {
        this.userForm.get('confirm_password')?.setValidators([]);
        this.userForm.validator = null;
      } else {
        this.userForm.get('confirm_password')?.setValidators([Validators.required]);
        this.userForm.validator = passwordMatchValidator;
      }
      this.userForm.updateValueAndValidity({emitEvent: false});
    })
  }

  private roleChangesSubscription(): void {
    this.userForm.get('role_id')?.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((role: any) => {
      this.applyRoleValidation(role);
    });
  }

  private applyRoleValidation(role: any): void {
    const roleId = role?.id ?? role;
    const isInstaller = roleId === 5;
    const isDesigner = roleId === 6;

    // Subcalendar: required for Installer only
    if (isInstaller) {
      this.userForm.get('subcalendar_id')?.setValidators([Validators.required]);
    } else {
      this.userForm.get('subcalendar_id')?.setValidators([]);
      this.userForm.get('subcalendar_id')?.setValue(null);
    }
    this.userForm.get('subcalendar_id')?.updateValueAndValidity({emitEvent: false});

    // Zoho + TeamUp: required for Designer only
    if (isDesigner) {
      this.userForm.get('zoho_contact_owner_id')?.setValidators([Validators.required]);
      this.userForm.get('teamup_job_owner_id')?.setValidators([Validators.required]);
    } else {
      this.userForm.get('zoho_contact_owner_id')?.setValidators([]);
      this.userForm.get('zoho_contact_owner_id')?.setValue(null);
      this.userForm.get('teamup_job_owner_id')?.setValidators([]);
      this.userForm.get('teamup_job_owner_id')?.setValue(null);
    }
    this.userForm.get('zoho_contact_owner_id')?.updateValueAndValidity({emitEvent: false});
    this.userForm.get('teamup_job_owner_id')?.updateValueAndValidity({emitEvent: false});
  }

  private getData(): void {
    forkJoin([
      this.usersService.getRoles().pipe(catchError(() => of({ data: [] }))),
      this.usersService.getInstallers().pipe(catchError(() => of({ data: [] }))),
      this.usersService.getZohoOwners().pipe(catchError(() => of({ data: [] }))),
      this.usersService.getTeamUpWho().pipe(catchError(() => of({ data: [] })))
    ]).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(([roles, installers, zohoOwners, teamUpWho]) => {
      // Keep modal usable even when one of the lookup endpoints is temporarily unavailable.
      this.roles = roles.data ?? [];
      this.installers = installers.data ?? [];
      this.zohoOwners = zohoOwners.data ?? [];
      this.teamUpWho = teamUpWho.data ?? [];
      this.buildForm();
    });
  }

  private handleInvalidFormSubmission(): void {
    this.userForm.markAllAsTouched();
    this.toastr.warning('Some fields are missing or invalid. Please review the form and try again.');
    this.scrollToFirstInvalidField();
  }

  private scrollToFirstInvalidField(): void {
    const firstInvalidField = document.querySelector('.modal-body .ng-invalid') as HTMLElement | null;
    if (!firstInvalidField) {
      return;
    }

    firstInvalidField.scrollIntoView({behavior: 'smooth', block: 'center'});
    firstInvalidField.focus?.();
  }
}
